import type { Dispatch, MutableRefObject } from "react";
import { useEffect, useRef, useState } from "react";
import type { DocTypeConfig, ResolvedInventoryDTO } from "../../api/client";
import { getDocTypeConfig } from "../../api/client";
import {
	isValidLibraryId,
	loadDocSelectionProps,
	loadWordInsertionMode,
} from "../../services/wordWriter";
import { lookupDoctypeChoice } from "../components/DoctypePicker";
import type { LoomFreshSelection } from "../components/LoomFresh";
import type { PlanData } from "../components/LoomPlanReview";
import type { LoomSelectionCommit } from "../components/LoomSelection";
import type { ChatMessage } from "../messageProjection";
import {
	CHAT_PAGE_SIZE,
	fetchDocumentChat,
	fetchDocumentHydrate,
	fetchDocumentSession,
	flushAllPendingSaves,
	getCachedDocId,
	initDocumentSession,
} from "../services/documentSessionService";
import type { RunIdentity } from "../sseAdapter";
import type { VariantCEvent } from "../state";
import { normalisePersistedSpineSnapshot } from "../utils/spineSnapshot";
import type { SpineSection } from "./useVariantCRun";

export interface UseSessionBootstrapParams {
	/** External shell setters that bootstrap seeds from the persisted session */
	setSelectedLibraryId: (id: string) => void;
	setSelectedDocCode: (code: string) => void;
	setActiveSelection: (s: LoomFreshSelection) => void;
	setDocConfig: (cfg: DocTypeConfig) => void;
	/** Which eCTD leaf this document IS, recovered from the persisted session.
	 *  Seeded on every bootstrap — `null` included, which is the answer for a
	 *  doctype filed as one document and for every document created before the
	 *  leaf split. Those must widen back to the whole doctype, so `null` is a
	 *  real value here, never "leave it alone". */
	setLeafDocumentId: (id: string | null) => void;
	setSectionCommit: (c: LoomSelectionCommit) => void;
	setPlanId: (id: string) => void;
	setPlanData: (d: PlanData) => void;
	setFocusedSectionId: (id: string) => void;
	dispatch: Dispatch<VariantCEvent>;
}

export interface UseSessionBootstrapResult {
		documentSessionId: string;
		setDocumentSessionId: (id: string) => void;
		sessionLoading: boolean;
		/** Workspace id recovered from the session; read by the workspace
		 *  fetch effect before React state is available. */
		sessionWorkspaceRef: MutableRefObject<string | null>;
		restoredIdentity: RunIdentity | null;
		bootstrapRunId: string | null;
		bootstrapDismissedRunId: string | null;
		persistedSpineSnapshot: SpineSection[] | null;
		setPersistedSpineSnapshot: (v: SpineSection[] | null) => void;
		persistedChatMessages: ChatMessage[];
		setPersistedChatMessages: (v: ChatMessage[]) => void;
		/** True when older chat pages exist on the backend beyond the
		 *  initial ``CHAT_PAGE_SIZE`` seed — drives the timeline's
		 *  scroll-up pagination. */
		persistedChatHasMore: boolean;
		setPersistedChatHasMore: (v: boolean) => void;
		restoredConversationId: string | undefined;
		setRestoredConversationId: (v: string | undefined) => void;
		restoredInsertionMode: "batch" | "section" | null;
		knownHadContent: boolean;
		setKnownHadContent: (v: boolean) => void;
		/** Library inventory snapshot restored from DocumentSessionDoc.
		 *  Hydrated into useWorkspaceLibrary so the post-bootstrap effect
		 *  doesn't re-stream when nothing has changed. Null on cold start
		 *  or when the prior session never resolved an inventory. */
		restoredInventorySnapshot: ResolvedInventoryDTO | null;
		restoredInventoryDoctype: string | null;
	}

export function useSessionBootstrap({
	setSelectedLibraryId,
	setSelectedDocCode,
	setActiveSelection,
	setDocConfig,
	setLeafDocumentId,
	setSectionCommit,
	setPlanId,
	setPlanData,
	setFocusedSectionId,
	dispatch,
}: UseSessionBootstrapParams): UseSessionBootstrapResult {
	const [documentSessionId, setDocumentSessionId] = useState<string>("");
	const [sessionLoading, setSessionLoading] = useState(true);
	const sessionWorkspaceRef = useRef<string | null>(null);
	const [restoredIdentity, setRestoredIdentity] = useState<RunIdentity | null>(
		null,
	);
	const [bootstrapRunId, setBootstrapRunId] = useState<string | null>(null);
	const [bootstrapDismissedRunId, setBootstrapDismissedRunId] = useState<
		string | null
	>(null);
	const [persistedSpineSnapshot, setPersistedSpineSnapshot] = useState<
		SpineSection[] | null
	>(null);
	const [persistedChatMessages, setPersistedChatMessages] = useState<
		ChatMessage[]
	>([]);
	const [persistedChatHasMore, setPersistedChatHasMore] = useState(false);
	const [restoredConversationId, setRestoredConversationId] = useState<
		string | undefined
	>();
	const [restoredInsertionMode, setRestoredInsertionMode] = useState<
		"batch" | "section" | null
	>(null);
	const [knownHadContent, setKnownHadContent] = useState(false);
	const [restoredInventorySnapshot, setRestoredInventorySnapshot] =
		useState<ResolvedInventoryDTO | null>(null);
	const [restoredInventoryDoctype, setRestoredInventoryDoctype] = useState<
		string | null
	>(null);

	// ----- Document session bootstrap --------------------------------------
	// Runs once on mount (component only mounts when logged in — App.tsx
	// gates on ``loggedIn``). Sequence:
	//   1. Resolve the Word document's stable AsthrDocumentId.
	//   2. Fetch the persisted session from the backend.
	//   3. If found: seed picker state (workspace, library, doctype,
	//      view mode, section commit, plan id).
	//   4. If found + chat data exists: merge backend chat into
	//      localStorage so cross-device history is available immediately.
	//   5. Always mark sessionLoading = false so the workspace effect
	//      can proceed with the correct workspace preference.
	//
	// Graceful degradation: any failure returns null and the normal
	// cold-start flow runs unchanged.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	// biome-ignore lint/correctness/useExhaustiveDependencies: mount-only — reading latest values via refs inside
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const docId = await initDocumentSession();
				if (cancelled || !docId) return;
				setDocumentSessionId(docId);

				// New restore path: one hydrate request provides the backend's
				// current document snapshot plus persisted messages. Keep the
				// legacy parallel fetch as a rollout fallback until the hydrate
				// path fully replaces the old bootstrap.
				const hydrated = await fetchDocumentHydrate(docId);
				// Fall back to the legacy session endpoint ONLY when the hydrate
				// endpoint itself failed (returns null) — when hydrate succeeded
				// but had no persisted session, that's an authoritative "cold
				// doc" answer and there's nothing for the fallback to recover.
				const session =
					hydrated?.session ??
					(hydrated === null ? await fetchDocumentSession(docId) : null);
				// Which leaf this document IS — seeded unconditionally, `null`
				// included, so a stale id from a previously-opened document can
				// never survive into this one.
				setLeafDocumentId(session?.leaf_document_id ?? null);
				const wordInsertionMode = await loadWordInsertionMode();
				setRestoredInsertionMode(
					wordInsertionMode ?? session?.insertion_mode ?? "batch",
				);
				// Refresh restore hydrates from persisted doc-level chat history.
				// This avoids replay-style reconstruction that can reorder messages.
				// First page only — the timeline pages backward on scroll-up.
				void fetchDocumentChat(docId, { limit: CHAT_PAGE_SIZE })
					.then((page) => {
						if (cancelled) return;
						setPersistedChatMessages(page?.messages ?? []);
						setPersistedChatHasMore(page?.hasMore ?? false);
					})
					.catch(() => {
						// Non-fatal — keep empty.
					});
				// Mark the doc as "once had content" if the hydrate payload
				// shows any project state — drives the empty-doc detector's
				// first-poll behaviour for reopened-empty docs.
				const sessHadContent = Boolean(
					session?.run_id ||
						session?.plan_id ||
						(Array.isArray(session?.spine_snapshot) &&
							session.spine_snapshot.length > 0),
				);
				if (sessHadContent) setKnownHadContent(true);
				console.info("[bootstrap-diag] hydrate applied", {
					docId,
					hasSession: Boolean(session),
					chatFetchScheduled: true,
					willRestoreSelection: Boolean(
						session?.library_id &&
							session?.library_name &&
							session?.doc_code &&
							session?.doc_name &&
							session?.backend_doc_type,
					),
				});
				if (cancelled || !session) return;
				// Doc-id rotation guard. ``docId`` was captured at mount; a
				// foreign/existing doc that the writer then chose to build on (or
				// start fresh on) has its ``AsthrDocumentId`` rotated mid-flight
				// (``rotateForeignDocIfNeeded`` → ``setCachedDocId(newId)``). This
				// async restore is still holding the OLD (foreign) session — whose
				// saved selection is the ORIGINAL owner's library — and applying it
				// here would clobber the custom-skip clear and paint a phantom
				// "committed" library the run doesn't actually have. If the cache
				// moved on, this bootstrap is for a stale doc: apply nothing.
				if (getCachedDocId() !== docId) return;

				const restoredSpineSnapshot = normalisePersistedSpineSnapshot(
					session.spine_snapshot,
				);
				setPersistedSpineSnapshot(restoredSpineSnapshot);

				// Stash workspace ref (read by workspace effect, not React state).
				if (session.workspace_id) {
					sessionWorkspaceRef.current = session.workspace_id;
				}

				// Seed (trial, session) from persistence so identity-dependent
				// consumers (selection citations, chat provenance fallback)
				// work on reopen. SSE ``run_metadata`` only fires for live
				// runs; without this the chip silently no-ops on reopened
				// drafts. See useSelectionCitations.canFetchSelection.
				if (session.trial && session.session_id) {
					setRestoredIdentity({
						trial: session.trial,
						session: session.session_id,
					});
				}

				const restoredDocName =
					session.doc_name ??
					(session.doc_code
						? (lookupDoctypeChoice(session.doc_code)?.docName ?? null)
						: null);
				console.info("[spine-refresh-debug] bootstrap session snapshot", {
					docId,
					runId: session.run_id ?? null,
					viewMode: session.view_mode,
					planId: session.plan_id ?? null,
					hasSelection: Boolean(
						session.library_id &&
							session.library_name &&
							session.doc_code &&
							restoredDocName &&
							session.backend_doc_type,
					),
					hasSectionCommit: Boolean(
						session.section_commit &&
							Array.isArray(session.section_commit.resolvedSectionIds),
					),
					persistedSpineSections: restoredSpineSnapshot?.length ?? 0,
				});

				// ── Seed picker state ─────────────────────────────────────────
				if (session.library_id) setSelectedLibraryId(session.library_id);
				if (session.doc_code) setSelectedDocCode(session.doc_code);
				// Hydrate the cached library inventory so useWorkspaceLibrary's
				// first effect can skip the SSE re-stream. Only honoured when
				// the snapshot's library_id matches the restored library_id —
				// a mismatch would mean the snapshot is stale (writer switched
				// libraries between sessions without our save catching up).
				if (
					session.library_inventory_snapshot &&
					session.library_id &&
					(session.library_inventory_snapshot as ResolvedInventoryDTO)
						.library_id === session.library_id
				) {
					setRestoredInventorySnapshot(
						session.library_inventory_snapshot as ResolvedInventoryDTO,
					);
					setRestoredInventoryDoctype(session.library_resolved_doctype ?? null);
				}
				if (session.run_id) {
					dispatch({ type: "VIEW_MODE_SET", mode: "spine" });
				} else if (session.view_mode === "timeline") {
					dispatch({ type: "VIEW_MODE_SET", mode: "timeline" });
				}
				// Restore docConfig on reopen — without it, ``initialSections``
				// stays empty and the spine never renders (and useRunControl's
				// run-restore effect bails on its ``sections.length === 0``
				// guard). The start-draft flow normally fetches this, but on
				// rehydration we never pass through that flow.
				//
				// CRITICAL: docConfig restore is keyed on the DOCTYPE, not on a
				// valid source library. A CUSTOM ("build on existing doc")
				// submission that skipped the source library persists
				// ``library_id = ""``; gating docConfig on ``isValidLibraryId``
				// left those runs with no seeded spine, so a just-imported custom
				// doc looked blank/unrecognized after logout → login. We still
				// prefer the full library-backed selection when present (template
				// runs), then Word custom props, then fall back to a
				// library-less, doctype-only selection so custom runs rehydrate.
				let docConfigLoaded = false;
				let resolvedBackendDocType: string | null = null;
				let resolvedDocCode: string | null = null;
				let resolvedDocName: string | null = null;
				let resolvedWorkspaceId: string | null = session.workspace_id ?? null;

				if (
					isValidLibraryId(session.library_id) &&
					session.library_name &&
					session.doc_code &&
					session.doc_name &&
					session.backend_doc_type
				) {
					setActiveSelection({
						workspaceId: session.workspace_id ?? null,
						libraryId: session.library_id,
						libraryName: session.library_name,
						docCode: session.doc_code,
						docName: session.doc_name,
						docFramework: session.doc_framework ?? undefined,
						backendDocType: session.backend_doc_type,
					});
					resolvedBackendDocType = session.backend_doc_type;
					try {
						const cfg = await getDocTypeConfig(session.backend_doc_type);
						if (!cancelled) {
							setDocConfig(cfg);
							docConfigLoaded = true;
						}
					} catch (err) {
						console.warn(
							"[useSessionBootstrap] docConfig restore failed on bootstrap:",
							err,
						);
					}
				} else {
					// Backend session can legitimately exist with sparse selection
					// fields (e.g. freshly opened downloaded docs, or custom
					// library-less imports). Try Word custom properties as a
					// secondary source of truth.
					const saved = await loadDocSelectionProps();
					resolvedWorkspaceId =
						saved?.workspaceId ?? session.workspace_id ?? null;
					resolvedBackendDocType =
						session.backend_doc_type ?? saved?.backendDocType ?? null;
					resolvedDocCode = session.doc_code ?? saved?.docCode ?? null;
					resolvedDocName =
						session.doc_name ??
						saved?.docName ??
						(resolvedDocCode
							? (lookupDoctypeChoice(resolvedDocCode)?.docName ?? null)
							: null);

					if (
						isValidLibraryId(saved?.libraryId) &&
						saved?.libraryName &&
						saved.docCode &&
						saved.backendDocType
					) {
						console.info(
							"[bootstrap-diag] selection restored from Word custom props",
							saved,
						);
						setActiveSelection({
							workspaceId: saved.workspaceId ?? session.workspace_id ?? null,
							libraryId: saved.libraryId,
							libraryName: saved.libraryName,
							docCode: saved.docCode,
							docName:
								saved.docName ??
								lookupDoctypeChoice(saved.docCode)?.docName ??
								saved.docCode,
							backendDocType: saved.backendDocType,
						});
						try {
							const cfg = await getDocTypeConfig(saved.backendDocType);
							if (!cancelled) {
								setDocConfig(cfg);
								docConfigLoaded = true;
							}
						} catch (err) {
							console.warn(
								"[useSessionBootstrap] docConfig restore (fallback) failed:",
								err,
							);
						}
					}
				}

				// Doctype-only fallback: a doctype is known but no valid library
				// was restored above — the custom-submission case. Seed docConfig
				// (so the spine renders and the run rehydrates) and a library-less
				// selection so the header stays consistent.
				if (!docConfigLoaded && resolvedBackendDocType) {
					setActiveSelection({
						workspaceId: resolvedWorkspaceId,
						libraryId: session.library_id ?? "",
						libraryName: session.library_name ?? "",
						docCode: resolvedDocCode ?? resolvedBackendDocType,
						docName:
							resolvedDocName ?? resolvedDocCode ?? resolvedBackendDocType,
						docFramework: session.doc_framework ?? undefined,
						backendDocType: resolvedBackendDocType,
					});
					try {
						const cfg = await getDocTypeConfig(resolvedBackendDocType);
						if (!cancelled) setDocConfig(cfg);
					} catch (err) {
						console.warn(
							"[useSessionBootstrap] docConfig restore (doctype-only) failed:",
							err,
						);
					}
				}
				if (
					session.section_commit &&
					Array.isArray(session.section_commit.resolvedSectionIds)
				) {
					setSectionCommit(
						session.section_commit as unknown as LoomSelectionCommit,
					);
				}
				if (session.plan_id) setPlanId(session.plan_id);
				if (session.conversation_id)
					setRestoredConversationId(session.conversation_id);
				if (
					hydrated?.plan?.plan_id === session.plan_id &&
					hydrated.plan.plan_payload
				) {
					setPlanData(hydrated.plan.plan_payload as unknown as PlanData);
				}

				// A persisted timeline_filter is not restored: filters were set
				// only by the retired /filter command, and nothing can clear one.
				// Do not restore focused_section_id on refresh.
				// Restoring it auto-opens the section draft panel; writer
				// expects that panel to open only after manual spine click.
				// Hand off run_id restoration to the run-restore effect at
				// the bottom of the bootstrap section. Doing it here would
				// race the docConfig fetch above — the run-restore effect
				// waits for ``sections`` (derived from docConfig) to seed.
				if (session.run_id) {
					setBootstrapRunId(session.run_id);
				}
				// Restore the dismissal marker so the run-restore effect skips
				// auto-resurrecting a run the writer reset before reload.
				if (session.dismissed_run_id) {
					setBootstrapDismissedRunId(session.dismissed_run_id);
				}
			} finally {
				if (!cancelled) setSessionLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []); // mount-only

	// ----- Flush debounced writes on close/reload -------------------------
	// ``keepalive`` requests survive the unload up to ~64 KB.
	// A live run is deliberately NOT paused here: drafting runs on the server
	// and needs no open pane, and the run-restore path re-attaches SSE and live
	// projection on reopen. Pausing on unload cancelled in-flight drafts (run
	// 6ac0eee8: two 3.2.P.3.5 attempts lost to a pane reload / re-login).
	useEffect(() => {
		window.addEventListener("beforeunload", flushAllPendingSaves);
		window.addEventListener("pagehide", flushAllPendingSaves);
		return () => {
			window.removeEventListener("beforeunload", flushAllPendingSaves);
			window.removeEventListener("pagehide", flushAllPendingSaves);
		};
	}, []); // mount-only

	return {
		documentSessionId,
		setDocumentSessionId,
		sessionLoading,
		sessionWorkspaceRef,
		restoredIdentity,
		bootstrapRunId,
		bootstrapDismissedRunId,
		persistedSpineSnapshot,
		setPersistedSpineSnapshot,
		persistedChatMessages,
		setPersistedChatMessages,
		persistedChatHasMore,
		setPersistedChatHasMore,
		restoredConversationId,
		setRestoredConversationId,
		restoredInsertionMode,
		knownHadContent,
		setKnownHadContent,
		restoredInventorySnapshot,
		restoredInventoryDoctype,
	};
}
